import React, { useMemo } from "react";

const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";

// 3x3 grid: Y = Performance (Low/Mid/High), X = Potential (Low/Mid/High)
const BOXES = [
  { row: 2, col: 0, label: "Solid Contributor",  desc: "High perf, low potential",   color: "#60a5fa"  },
  { row: 2, col: 1, label: "High Performer",      desc: "High perf, mid potential",   color: "#34d399"  },
  { row: 2, col: 2, label: "Star / Top Talent",   desc: "High perf, high potential",  color: "#00ff90"  },
  { row: 1, col: 0, label: "Effective",           desc: "Mid perf, low potential",    color: "#fbbf24"  },
  { row: 1, col: 1, label: "Core Player",         desc: "Mid perf, mid potential",    color: "#fbbf24"  },
  { row: 1, col: 2, label: "Future Star",         desc: "Mid perf, high potential",   color: "#34d399"  },
  { row: 0, col: 0, label: "Under Performer",     desc: "Low perf, low potential",    color: "#f87171"  },
  { row: 0, col: 1, label: "Inconsistent Player", desc: "Low perf, mid potential",    color: "#fb923c"  },
  { row: 0, col: 2, label: "Enigma",              desc: "Low perf, high potential",   color: "#c084fc"  },
];

function classify(r) {
  const rating = r.rating || 0;
  const pot    = (r.potential || "M").toUpperCase()[0];
  const perfRow = rating >= 4 ? 2 : rating >= 3 ? 1 : 0;
  const potCol  = pot === "H" ? 2 : pot === "L" ? 0 : 1;
  return BOXES.find(b => b.row === perfRow && b.col === potCol);
}

export default function NineBoxMatrix({ rows }) {
  const groups = useMemo(() => {
    const g = {};
    BOXES.forEach(b => { g[`${b.row}-${b.col}`] = []; });
    (rows || []).forEach(r => {
      const box = classify(r);
      if (box) g[`${box.row}-${box.col}`].push(r);
    });
    return g;
  }, [rows]);

  return (
    <div className={card}>
      <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
        <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">9-Box Matrix</span>
        <span className="text-xs text-white/30">{rows?.length || 0} people</span>
      </div>
      <div className="p-5">
        {/* Axis labels */}
        <div className="flex mb-1">
          <div className="w-12 shrink-0" />
          {["Low Potential","Mid Potential","High Potential"].map(l => (
            <div key={l} className="flex-1 text-center text-xs text-white/25">{l}</div>
          ))}
        </div>

        <div className="flex flex-col gap-1">
          {[2, 1, 0].map(row => (
            <div key={row} className="flex gap-1 items-stretch">
              <div className="w-12 shrink-0 flex items-center">
                <span className="text-xs text-white/25 -rotate-90 whitespace-nowrap" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
                  {row === 2 ? "High Perf" : row === 1 ? "Mid Perf" : "Low Perf"}
                </span>
              </div>
              {[0, 1, 2].map(col => {
                const box   = BOXES.find(b => b.row === row && b.col === col);
                const people = groups[`${row}-${col}`] || [];
                return (
                  <div key={col} className="flex-1 rounded-lg border border-white/5 bg-white/3 p-2 min-h-[80px]"
                    style={{ borderColor: people.length > 0 ? `${box.color}25` : undefined }}>
                    <p className="text-xs font-medium mb-1" style={{ color: box.color }}>{box.label}</p>
                    <p className="text-xs text-white/20 mb-2 leading-tight">{box.desc}</p>
                    {people.length === 0 ? (
                      <span className="text-xs text-white/15">—</span>
                    ) : (
                      <div className="space-y-0.5 max-h-20 overflow-y-auto">
                        {people.map(p => (
                          <p key={p.id} className="text-xs text-white/50 truncate">
                            {p.name}
                            {p.rating && <span className="text-white/25 ml-1">({parseFloat(p.rating).toFixed(1)})</span>}
                          </p>
                        ))}
                      </div>
                    )}
                    {people.length > 0 && (
                      <div className="mt-1 text-xs font-bold" style={{ color: box.color }}>{people.length}</div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}